import Intercom, { boot, shutdown, update } from "@intercom/messenger-js-sdk";
import type { IntercomSettings } from "@intercom/messenger-js-sdk/types";
import { usePrevious } from "@mantine/hooks";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import { useEffect, useMemo } from "react";

import type { Nullable } from "@/types/utils";
import type { User } from "@/types/user";

// We only have the one app ID for all environments. Always available on client
// side because Intercom only supports loading on client side.
const INTERCOM_APP_ID = "oijmxz0i";

const getUserCreatedAtEpochSeconds = (user: Nullable<User>) => {
  const createdAt = user?.createdAt?.valueOf();

  // Intercom expects seconds since epoch rather than JS MS since epoch
  return createdAt ? createdAt / 1000 : undefined;
};

export const useIntercom = (user: Nullable<User>) => {
  const previousUser = usePrevious(user);
  const intercomSettings: IntercomSettings = useMemo(
    () => ({
      app_id: INTERCOM_APP_ID,
      user_id: user?.id,
      name: user?.fullName,
      email: user?.email,
      created_at: getUserCreatedAtEpochSeconds(user),
    }),
    [user],
  );

  // This feels wrong to me, but the docs say to just stick in the render cycle
  // ¯\_(ツ)_/¯
  // https://developers.intercom.com/installing-intercom/web/installation
  Intercom(intercomSettings);

  useEffect(() => {
    // Login/Auth Init
    if (user && !previousUser) {
      return boot(intercomSettings);
    }

    // Logout
    if (!user && previousUser) {
      return shutdown();
    }

    // User properties change
    if (!isEqual(user, previousUser)) {
      // update will accept any props, but is technically only interested in
      // user data. Avoid tacking on unnecessary stuff with omit.
      return update(omit(intercomSettings, ["app_id"]));
    }
  }, [intercomSettings, previousUser, user]);
};
